import * as React from "react";
import { Outlet } from "react-router";

import { GlobalShortcuts } from "@/components/layout/CommandPalette";
import { Header } from "@/components/layout/Header";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { MobileTopBar } from "@/components/layout/MobileFrame";
import { PageTransition } from "@/components/layout/PageTransition";
import { Sidebar } from "@/components/layout/Sidebar";
import { WelcomeOnLanding } from "@/components/onboarding/WelcomeModal";
import { UnsavedChangesGuard } from "@/components/ui/unsaved-changes-dialog";
import { DetailsProvider } from "@/lib/details";
import { useGlobalShortcuts } from "@/lib/shortcuts";
import { usePageTitle } from "@/lib/usePageTitle";
import { useViewport } from "@/lib/useViewport";
import { usePersistRoute } from "@/native/useLastRoute";
import { isMobileBuild } from "@/platform";

/**
 * One desktop shell for every persona — grouped rail + 52px top bar
 * (MV2-845); the rail shows each persona only its own items. The Site Owner
 * had their own frame (MV2-846) until MV2-1261 (Kranthi, 6 Oct: "can we make
 * the layout consistent?") — no theme switch, a narrower body, a Reports
 * link they couldn't use. Phones get the 48px bar and the bottom nav for
 * every persona. A SuperAdmin acting as a tenant keeps the shell in the
 * tenant's colours with the banner above it.
 */
export function AppShell(): React.ReactElement {
  const { isCompact } = useViewport();
  usePersistRoute();
  usePageTitle();
  // MV2-845: g-leader, ⌘K, ?, / and n — page keys stay on their pages.
  useGlobalShortcuts();

  return (
    // MV2-838: the page-level Details switch lives here so every authenticated
    // surface shares one ?detail=1 state and every <Fold> below can read it.
    <DetailsProvider>
      <div
        className="flex h-full w-full overflow-hidden"
        // On native iOS, the status bar overlaps the WebView.
        style={
          isMobileBuild ? { paddingTop: "env(safe-area-inset-top)" } : undefined
        }
      >
        {isCompact ? (
          <div className="flex min-w-0 flex-1 flex-col">
            <ImpersonationBanner />
            <MobileTopBar />
            {/* Bottom padding equals the nav bar's full rendered height so the
                last row of content is never hidden behind the fixed bar. */}
            <main className="min-h-0 flex-1 overflow-hidden pb-[calc(56px+env(safe-area-inset-bottom))]">
              <PageTransition>
                <Outlet />
              </PageTransition>
            </main>
            <MobileBottomNav />
          </div>
        ) : (
          <>
            <Sidebar />
            <div className="flex min-w-0 flex-1 flex-col">
              <ImpersonationBanner />
              <Header />
              <main className="min-h-0 flex-1 overflow-hidden">
                <PageTransition>
                  <Outlet />
                </PageTransition>
              </main>
            </div>
          </>
        )}
        {/* MV2-843: one "Leave without saving?" for every dirty form below. */}
        <UnsavedChangesGuard />
        {/* MV2-911: the first-sign-in welcome, once, on the persona's landing route. */}
        <WelcomeOnLanding />
        <GlobalShortcuts />
      </div>
    </DetailsProvider>
  );
}
